import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import styled from "styled-components";
import firebase from "firebase";
import ActivityPage, { Copy, Heading, Muted, PrimaryButton } from "./ActivityPage";

const Email = styled.strong`
	display: block;
	margin-top: 8px;
	word-break: break-all;
`;

type Status = "checking" | "ready" | "verifying" | "success" | "error";

const EXPIRED_MESSAGE = "This verification link has expired. Please request a new one from the app.";

interface VerifyEmailProps {
	app: firebase.app.App;
	mode: string | null;
	oobCode: string | null;
}

const VerifyEmail: React.FC<VerifyEmailProps> = ({ app, mode, oobCode }) => {
	const [status, setStatus] = useState<Status>("checking");
	const [email, setEmail] = useState<string | null>(null);
	const [errorMessage, setErrorMessage] = useState<string>("");

	useEffect(() => {
		if (mode !== "verifyEmail" || !oobCode) {
			setStatus("error");
			setErrorMessage("This verification link is invalid.");
			return;
		}

		app.auth()
			.checkActionCode(oobCode)
			.then(info => {
				if (info.operation !== "VERIFY_EMAIL") {
					setStatus("error");
					setErrorMessage(
						"This link isn't an email verification link. Please use the link from your most recent verification email."
					);
					return;
				}
				setEmail(info.data.email || null);
				setStatus("ready");
			})
			.catch(err => {
				setStatus("error");
				setErrorMessage(
					err && err.code === "auth/expired-action-code"
						? EXPIRED_MESSAGE
						: "This verification link is invalid or has already been used."
				);
			});
	}, [app, mode, oobCode]);

	const handleVerify = async () => {
		if (!oobCode) return;
		setStatus("verifying");
		try {
			await app.auth().applyActionCode(oobCode);
			setStatus("success");
		} catch (err: any) {
			setStatus("error");
			setErrorMessage(
				err && err.code === "auth/expired-action-code"
					? EXPIRED_MESSAGE
					: "We couldn't verify your email. The link may have already been used."
			);
		}
	};

	if (status === "checking") {
		return <Muted>Loading…</Muted>;
	}
	if (status === "success") {
		return (
			<>
				<Heading>Email verified</Heading>
				<p style={{ margin: 0 }}>You're all set. You can close this page and return to the app.</p>
			</>
		);
	}
	if (status === "error") {
		return (
			<>
				<Heading>Unable to verify</Heading>
				<p style={{ margin: 0 }}>{errorMessage}</p>
			</>
		);
	}
	return (
		<>
			<Heading>Verify your email</Heading>
			<Copy>Click the button below to verify{email ? <Email>{email}</Email> : " your email address"}</Copy>
			<PrimaryButton type="button" onClick={handleVerify} disabled={status === "verifying"}>
				{status === "verifying" ? "Verifying…" : "Verify my email"}
			</PrimaryButton>
		</>
	);
};

/**
 * /activity/verify-email/:appKey?mode=verifyEmail&oobCode=...
 *
 * Target of the verification emails sent by each white-label app's Cloud
 * Functions. Applies the Firebase action code against that app's project.
 */
const VerifyEmailActivity: React.FC = () => {
	const { appKey } = useParams<{ appKey: string }>();
	const { search } = useLocation();
	const params = new URLSearchParams(search);

	return (
		<ActivityPage title="Verify your email" appKey={appKey}>
			{app => <VerifyEmail app={app} mode={params.get("mode")} oobCode={params.get("oobCode")} />}
		</ActivityPage>
	);
};

export default VerifyEmailActivity;
